import { useState, useMemo, useCallback } from "react";
import { type Row } from "@tanstack/react-table";
import { type ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { AppCard } from "../../../../components/common/card";
import { PaginationTable } from "./pagination-table";
import { SEARCH_MAX_LENGTH, useSearchFiltersUrlSync } from "../../hooks/useSearchFiltersUrlSync";
import { SearchBar } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { Drawer } from "../../../../components/common/drawer";
import type { EmptyStateProps } from "../../../../components/common/empty-state";
import type { PaginatedResponse } from "../../data";
import { IconFilter, IconSearch } from "@tabler/icons-react";
// Type for hook options that can have params and SWR configuration
interface FilterComponentProps<TFilter> {
    filters: TFilter;
    onFiltersChange: (filters: TFilter) => void;
}
interface PaginationAppCardProps<T, TQuery> {
    // AppCard props
    title?: string;
    color?: string;
    borderColor?: string;
    className?: string;
    headerAction?: React.ReactNode;
    backButton?: boolean | string;
    // Search & Filter props
    enableSearch?: boolean;
    searchPlaceholder?: string;
    filterComponent?: ReactNode | ((props: FilterComponentProps<TQuery>) => ReactNode);
    filterTitle?: string;
    initialFilters?: TQuery;
    /** URL params owned by the host page (e.g. `tab`); see useSearchFiltersUrlSync. */
    preservedUrlParams?: readonly string[];
    // PaginationTable props
    useDataHook: (query?: TQuery) => {
        data?: PaginatedResponse<T>;
        isLoading: boolean;
        error?: Error;
    };
    columns: ColumnDef<T>[];
    pageSize?: number;
    emptyMessage?: string;
    emptyState?: EmptyStateProps;
    topComponent?: React.ReactNode;
    onRowClick?: (row: Row<T>) => void;
}
export function PaginationAppCard<T, TQuery>({ title, color, borderColor, className, headerAction, backButton, enableSearch = false, searchPlaceholder = "Search...", filterComponent, filterTitle = "Filter", initialFilters = {} as TQuery, preservedUrlParams, useDataHook, columns, pageSize = 10, emptyMessage = "No data found", emptyState, topComponent, onRowClick, }: PaginationAppCardProps<T, TQuery>) {
    const { searchValue, setSearchValue, filters, setFilters, baseQuery } = useSearchFiltersUrlSync<TQuery>({
        initialFilters: initialFilters as Record<string, unknown>,
        enableSearch,
        preservedParams: preservedUrlParams,
    });
    const [filterOpen, setFilterOpen] = useState(false);
    // Default filter count function - counts all non-empty filter values
    // Excludes pagination parameters (page, pageSize), sorting parameters (sortBy, sortOrder), and search
    const calculateFilterCount = useCallback((filtersToCount: TQuery): number => {
        let count = 0;
        const excludedKeys = ["page", "pageSize", "sortBy", "sortOrder", "search"];
        Object.entries(filtersToCount as Record<string, unknown>).forEach(([key, value]) => {
            // Skip pagination, sorting, and search parameters
            if (excludedKeys.includes(key)) {
                return;
            }
            if (value !== undefined && value !== null && value !== "") {
                if (Array.isArray(value)) {
                    count += value.length;
                }
                else {
                    count += 1;
                }
            }
        });
        return count;
    }, []);
    const activeFilterCount = calculateFilterCount(filters);
    // A narrowed list that comes back empty isn't "no data yet" — say so.
    const isNarrowed = activeFilterCount > 0 || (enableSearch && searchValue.trim() !== "");
    const effectiveEmptyState: EmptyStateProps | undefined = emptyState && isNarrowed
        ? {
            icon: IconSearch,
            size: emptyState.size,
            title: "No results found",
            description: "Try a different search term or clear the filters.",
        }
        : emptyState;
    // Build header actions with search and filter
    const builtHeaderActions = useMemo(() => {
        if (!enableSearch && !filterComponent && !headerAction) {
            return undefined;
        }
        return (
          <div className="flex items-center gap-3 max-md:flex-wrap">
            {enableSearch && (
              <div className="min-w-[280px] flex-1 lg:max-w-[420px] max-md:min-w-0 max-md:basis-full">
                <SearchBar
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={searchPlaceholder}
                  maxLength={SEARCH_MAX_LENGTH}
                />
              </div>
            )}
            {filterComponent && (
              <Button
                variant="secondary"
                size="md"
                leftIcon={<IconFilter size={16} />}
                onClick={() => setFilterOpen(true)}
              >
                Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </Button>
            )}
            {headerAction}
          </div>
        );
    }, [
        enableSearch,
        searchValue,
        setSearchValue,
        searchPlaceholder,
        filterComponent,
        activeFilterCount,
        headerAction,
    ]);
    const handleApplyFilters = (newFilters: TQuery) => {
        setFilters(newFilters);
        setFilterOpen(false);
    };
    return (<>
      <AppCard title={title} color={color} borderColor={borderColor} className={className} headerAction={builtHeaderActions} backButton={backButton}>
        <PaginationTable<T, TQuery> useDataHook={useDataHook} columns={columns} baseQuery={baseQuery} pageSize={pageSize} emptyMessage={emptyMessage} emptyState={effectiveEmptyState} topComponent={topComponent} onRowClick={onRowClick}/>
      </AppCard>

      {filterComponent && (<Drawer open={filterOpen} close={() => setFilterOpen(false)} title={filterTitle} content={typeof filterComponent === "function"
                ? filterComponent({
                    filters,
                    onFiltersChange: handleApplyFilters,
                })
                : filterComponent}/>)}
    </>);
}
